import React from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';

/** Shared easing for page/content entrances — a fast, decelerating curve. */
export const appEase: [number, number, number, number] = [0.22, 1, 0.36, 1];

/** Spring used for tap feedback and active-state pops across the app chrome. */
export const appSpring = { type: 'spring' as const, stiffness: 500, damping: 34 };

/**
 * Player-facing surfaces get the app-like route crossfade. Director, broadcast
 * (OBS overlays), tenant shadow sites, and /public/* (which lives under
 * OrganizationProvider — a keyed remount would refetch it per navigation)
 * are intentionally excluded.
 */
const ANIMATED_PATHS = /^\/(discover$|player\/|coach\/)/;

/**
 * Scrolls the window back to the top on forward navigations (PUSH/REPLACE),
 * like a native app pushing a new screen. Back/forward (POP) is left to the
 * browser's own scroll restoration.
 */
export const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  const navType = useNavigationType();

  React.useLayoutEffect(() => {
    if (navType !== 'POP') {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    }
  }, [pathname, navType]);

  return null;
};

/**
 * Route-level crossfade for player-facing pages. Opacity only — transforms on
 * an ancestor would re-anchor the fixed bottom tab bars rendered inside pages.
 */
export const RouteTransitions: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const reduceMotion = useReducedMotion();
  const animated = !reduceMotion && ANIMATED_PATHS.test(pathname);

  return (
    <motion.div
      key={animated ? pathname : 'static'}
      initial={animated ? { opacity: 0 } : false}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

/**
 * Content-level entrance: fade up into place. Wrap a page's main content
 * (never elements that need position: fixed) for a native push-in feel.
 */
export const PageTransition: React.FC<{
  children: React.ReactNode;
  className?: string;
  delay?: number;
}> = ({ children, className, delay = 0 }) => {
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      className={className}
      initial={reduceMotion ? false : { opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: appEase, delay }}
    >
      {children}
    </motion.div>
  );
};
